/**
 * Hooks de TanStack Query para PR (Purchase Requisition).
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { buildRepos } from "@/core/data/repos";
import { registrarAuditoria } from "@/core/audit/registrarAuditoria";
import { notificarCreacionPr, notificarTransicionPr } from "@core/mail/cableado";
import type { Pr } from "@app-types/entities";

export function usePrs(tenantId: string) {
  return useQuery({
    queryKey: ["prs", tenantId],
    queryFn: () => buildRepos(tenantId).pr.list(),
    staleTime: 30_000,
  });
}

export function usePr(tenantId: string, id: string | null) {
  return useQuery({
    queryKey: ["pr", tenantId, id],
    queryFn: () => (id ? buildRepos(tenantId).pr.get(id) : Promise.resolve(null)),
    enabled: !!id,
  });
}

export function usePrsByAfe(tenantId: string, afeId: string | null) {
  return useQuery({
    queryKey: ["prs-by-afe", tenantId, afeId],
    queryFn: () => (afeId ? buildRepos(tenantId).pr.listByAfe(afeId) : Promise.resolve([])),
    enabled: !!afeId,
  });
}

export function useCreatePr(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: Omit<Pr, "id" | "createdAt" | "updatedAt" | "version"> & {
        /** Opcional: nombre del usuario para usarlo en el correo PR-1. */
        _actorName?: string;
      },
    ) => {
      const { _actorName, ...prData } = data;
      const repo = buildRepos(tenantId).pr;
      const id = await repo.create(prData);
      // PR-1: notificar al PL del AFE cuando se crea la PR.
      const pr = await repo.get(id);
      if (pr) {
        void notificarCreacionPr({
          tenantId,
          pr,
          actor: {
            email: pr.solicitadoPor,
            ...(_actorName !== undefined && { nombre: _actorName }),
          },
        });
      }
      return id;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["prs", tenantId] });
      void qc.invalidateQueries({ queryKey: ["prs-by-afe", tenantId] });
    },
  });
}

export function useUpdatePr(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, version }: { id: string; data: Partial<Pr>; version: number }) =>
      buildRepos(tenantId).pr.update(id, data, version),
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: ["pr", tenantId, id] });
      void qc.invalidateQueries({ queryKey: ["prs", tenantId] });
      void qc.invalidateQueries({ queryKey: ["prs-by-afe", tenantId] });
    },
  });
}

export function useChangePrState(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      nextState,
      userEmail,
      userName,
      observaciones,
    }: {
      id: string;
      nextState: Pr["estado"];
      userEmail: string;
      /** Nombre del usuario logueado para personalizar el correo. */
      userName?: string;
      observaciones?: string;
    }) => {
      const repo = buildRepos(tenantId).pr;
      const antes = await repo.get(id);
      const estadoAnterior = antes?.estado;
      await repo.changeState(id, nextState, userEmail, observaciones);
      const despues = await repo.get(id);
      if (despues && estadoAnterior) {
        void registrarAuditoria(tenantId, {
          usuario: userEmail,
          accion: `PR_${nextState}`,
          entidad: "PR",
          entidadId: despues.codigo ?? id,
          diff: {
            estadoAnterior,
            estadoNuevo: nextState,
            ...(observaciones ? { motivo: observaciones } : {}),
          },
        });
        void notificarTransicionPr({
          tenantId,
          pr: despues,
          estadoAnterior,
          estadoNuevo: nextState,
          actor: { email: userEmail, ...(userName !== undefined && { nombre: userName }) },
          ...(observaciones !== undefined && { motivo: observaciones }),
        });
      }
    },
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: ["pr", tenantId, id] });
      void qc.invalidateQueries({ queryKey: ["prs", tenantId] });
    },
  });
}

export function useDeletePr(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => buildRepos(tenantId).pr.delete(id),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: ["pr", tenantId, id] });
      void qc.invalidateQueries({ queryKey: ["prs", tenantId] });
      void qc.invalidateQueries({ queryKey: ["prs-by-afe", tenantId] });
    },
  });
}
